import { useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  AppBar as MuiAppBar,
  AppBarProps as MuiAppBarProps,
  Drawer as MuiDrawer,
  styled,
  Toolbar,
  IconButton,
  Divider,
  Container,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import AppNavigation from '../components/AppNavigation/AppNavigation.component'
import UserService from '../services/UserService/UserService'

type AppLayoutContextType = {
  setPageName: (newPageName?: string) => void
}
export const AppLayoutContext: AppLayoutContextType = {
  setPageName: () => {
    // if used outside of AppLayout, this function will not have been replaced so throw error
    throw new Error('AppLayoutContext must be used within an AppLayout')
  },
}

const drawerWidth = 240
const collapsedDrawerWidth = 80

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  color: '#263238',
  boxShadow: '0 2px 8px rgba(0, 124, 145, 0.08)',
  borderBottom: '1px solid rgba(0, 124, 145, 0.12)',
  zIndex: theme.zIndex.drawer + 1,
  width: '100%',
}))

// Desktop permanent drawer (completely hidden on mobile)
const DesktopDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
      width: open ? drawerWidth : collapsedDrawerWidth,
      flexShrink: 0,
      whiteSpace: 'nowrap',
      boxSizing: 'border-box',
    },
    '& .MuiDrawer-paper': {
      position: 'fixed',
      width: open ? drawerWidth : collapsedDrawerWidth,
      top: '70px',
      height: 'calc(100vh - 70px)',
      color: '#263238',
      backgroundColor: '#FFFFFF',
      borderRight: '1px solid rgba(0, 124, 145, 0.12)',
      boxShadow: '2px 0 12px rgba(0, 124, 145, 0.08)',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: 'border-box',
      overflowX: 'hidden',
      // Hide the fixed-position paper on mobile (position:fixed escapes display:none on parent)
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
      ...(!open && {
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      }),
    },
  }),
)

const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated } = useAuth0()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const formattedUser = user ? UserService.formatUser(user) : undefined
  const [, setPageName] = useState('')
  const [open, setOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  const toggleDrawer = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen)
    } else {
      setOpen(!open)
    }
  }

  const handleMobileClose = () => {
    setMobileOpen(false)
  }

  AppLayoutContext.setPageName = (newPageName = '') => {
    setPageName(newPageName)
  }

  return (
    <Box sx={{ display: 'flex' }}>
      {isAuthenticated && formattedUser && (
        <>
          <AppBar position="fixed" open={open} elevation={0}>
            <Toolbar
              sx={{
                pr: '24px',
                minHeight: '70px !important',
                px: { xs: 1.5, sm: 3 },
                justifyContent: 'space-between',
              }}
            >
              {/* Hamburger menu — visible on mobile in the top bar */}
              {isMobile && (
                <IconButton
                  onClick={toggleDrawer}
                  sx={{
                    color: '#007C91',
                    mr: 1,
                    '&:hover': { backgroundColor: '#E0F7FA' },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Box
                component="img"
                src="https://scrubstr-wp-uploads.s3.us-east-2.amazonaws.com/scrubstrLogo.png"
                alt="Scrubstr Logo"
                onClick={() => navigate('/')}
                sx={{
                  height: { xs: 32, sm: 40 },
                  maxHeight: 40,
                  width: 'auto',
                  maxWidth: { xs: 140, sm: 200 },
                  cursor: 'pointer',
                  objectFit: 'contain',
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flexGrow: 1 }} />
              <Box>
                {formattedUser.picture ? (
                  <Avatar
                    alt={formattedUser.name}
                    src={formattedUser.picture}
                    sx={{
                      width: 40,
                      height: 40,
                      border: '2px solid #007C91',
                      boxShadow: '0 2px 8px rgba(0, 124, 145, 0.15)',
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      bgcolor: '#007C91',
                      width: 40,
                      height: 40,
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'white',
                      boxShadow: '0 2px 8px rgba(0, 124, 145, 0.15)',
                    }}
                  >
                    {formattedUser.name ? formattedUser.name[0].toUpperCase() : 'A'}
                  </Avatar>
                )}
              </Box>
            </Toolbar>
          </AppBar>

          {/* ── Mobile Drawer (temporary overlay) ── */}
          <MuiDrawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleMobileClose}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                top: 0,
                height: '100vh',
                color: '#263238',
                backgroundColor: '#FFFFFF',
                borderRight: '1px solid rgba(0, 124, 145, 0.12)',
                boxShadow: '2px 0 16px rgba(0, 124, 145, 0.15)',
                boxSizing: 'border-box',
              },
            }}
          >
            <Toolbar
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                minHeight: '70px !important',
                borderBottom: '1px solid rgba(0, 124, 145, 0.12)',
              }}
            >
              <Box
                component="img"
                src="https://scrubstr-wp-uploads.s3.us-east-2.amazonaws.com/scrubstrLogo.png"
                alt="Scrubstr Logo"
                sx={{ height: 30, width: 'auto', objectFit: 'contain' }}
              />
              <IconButton
                onClick={handleMobileClose}
                sx={{ color: '#007C91', '&:hover': { backgroundColor: '#E0F7FA' } }}
              >
                <CloseIcon />
              </IconButton>
            </Toolbar>
            <Divider sx={{ borderColor: 'rgba(0, 124, 145, 0.12)' }} />
            {/* Always pass open=true so labels show in mobile drawer */}
            <AppNavigation open={true} onNavigateComplete={handleMobileClose} />
          </MuiDrawer>

          {/* ── Desktop Drawer (permanent, collapsible) ── */}
          <DesktopDrawer variant="permanent" open={open}>
            <Toolbar
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: open ? 'flex-start' : 'center',
                px: open ? 2 : 0,
                minHeight: '70px !important',
                borderBottom: '1px solid rgba(0, 124, 145, 0.12)',
              }}
            >
              <IconButton
                onClick={toggleDrawer}
                sx={{
                  color: '#007C91',
                  width: open ? 'auto' : '100%',
                  borderRadius: 0,
                  '&:hover': { backgroundColor: '#E0F7FA' },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Toolbar>
            {open && <Divider sx={{ borderColor: 'rgba(0, 124, 145, 0.12)' }} />}
            <AppNavigation open={open} />
          </DesktopDrawer>
        </>
      )}
      <Box
        component="main"
        sx={(theme) => ({
          flexGrow: 1,
          background: '#F0F4F8',
          minHeight: '100vh',
          overflow: 'hidden',
          marginTop: '70px',
          width: '100%',
          maxWidth: '100vw',
          // Responsive padding: tight on mobile, spacious on desktop
          px: { xs: 1, sm: 2, md: 4, lg: '50px' },
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        })}
      >
        <Container maxWidth="xl" disableGutters sx={{ my: 2, px: { xs: 0.5, sm: 2 }, overflow: 'hidden' }}>
          {children}
        </Container>
      </Box>
    </Box>
  )
}
export default AppLayout
